import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";
import logoFreelaDash from "@/assets/logo-freeladash.png";

export default function RefundPage() {
  return (
    <>
      <SEOHead 
        title="Política de Cancelamento e Reembolso"
        description="Política de cancelamento e reembolso do FreelaDash. Saiba como cancelar sua assinatura e solicitar reembolso."
        canonical="/cancelamento"
      />
      <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto flex h-14 items-center justify-between px-4 sm:h-16">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={logoFreelaDash} 
              alt="FreelaDash" 
              className="h-8 w-8 rounded-lg object-contain"
            />
            <span className="font-display text-xl font-bold tracking-wide text-foreground" style={{ WebkitTextStroke: '0.5px currentColor' }}>
              FREELADASH
            </span>
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 sm:py-12">
        <article className="mx-auto max-w-3xl">
          <h1 className="mb-2 text-3xl font-bold sm:text-4xl">POLÍTICA DE CANCELAMENTO E REEMBOLSO</h1>
          <p className="mb-8 text-sm text-muted-foreground">Última atualização: 06 de fevereiro de 2026</p>

          <div className="prose prose-gray max-w-none space-y-6 text-foreground">
            <section>
              <h2 className="text-xl font-semibold text-foreground">1. Visão Geral</h2>
              <p className="text-muted-foreground leading-relaxed">
                Esta política descreve os termos de cancelamento de assinatura e reembolso do FreelaDash. 
                Nosso objetivo é ser transparente e justo em todas as transações com nossos usuários.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">2. Plano Gratuito</h2>
              <p className="text-muted-foreground leading-relaxed">
                O plano gratuito do FreelaDash pode ser usado indefinidamente sem nenhum custo. 
                Não há necessidade de fornecer dados de pagamento para usar o plano gratuito.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">3. Cancelamento do Plano Pro</h2>
              
              <h3 className="text-lg font-medium text-foreground mt-4">3.1 Como cancelar</h3>
              <p className="text-muted-foreground leading-relaxed">
                Você pode cancelar sua assinatura do plano Pro a qualquer momento, de duas formas:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Pelo painel:</strong> Acesse Configurações → Plano → Gerenciar Assinatura</li>
                <li><strong>Por email:</strong> Envie uma solicitação para contato@freeladash.com.br</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-4">3.2 O que acontece após o cancelamento</h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Sua assinatura não será renovada no próximo ciclo</li>
                <li><strong>Você mantém acesso ao plano Pro até o final do período já pago</strong></li>
                <li>Após o término do período, sua conta volta automaticamente para o plano gratuito</li>
                <li>Seus dados permanecem salvos e acessíveis no plano gratuito (respeitando os limites do plano)</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mt-4">3.3 Reativação</h3>
              <p className="text-muted-foreground leading-relaxed">
                Você pode reativar sua assinatura Pro a qualquer momento através do painel de configurações. 
                A cobrança será retomada imediatamente e você terá acesso aos recursos Pro.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">4. Política de Reembolso</h2>
              
              <h3 className="text-lg font-medium text-foreground mt-4">4.1 Regra geral</h3>
              <p className="text-muted-foreground leading-relaxed">
                <strong>Não oferecemos reembolso proporcional após a cobrança ser processada.</strong> 
                Quando você cancela, mantém acesso até o final do período pago, mas não há devolução 
                do valor referente aos dias não utilizados.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-4">4.2 Exceções</h3>
              <p className="text-muted-foreground leading-relaxed">
                Podemos considerar reembolso em casos excepcionais:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Cobrança duplicada por erro técnico</li>
                <li>Cobrança após cancelamento confirmado</li>
                <li>Indisponibilidade prolongada do serviço por nossa responsabilidade</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Para solicitar análise de reembolso excepcional, entre em contato em até 7 dias 
                após a cobrança, informando o motivo e comprovantes.
              </p>

              <h3 className="text-lg font-medium text-foreground mt-4">4.3 Prazo de processamento</h3>
              <p className="text-muted-foreground leading-relaxed">
                Reembolsos aprovados são processados em até 10 dias úteis. O prazo para o valor 
                aparecer em sua conta depende da sua operadora de cartão (geralmente 1-2 faturas).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">5. Alteração de Plano</h2>
              <p className="text-muted-foreground leading-relaxed">
                Se você mudar do plano mensal para anual (ou vice-versa), o novo valor será aplicado 
                proporcionalmente ou na próxima renovação, dependendo do momento da mudança.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">6. Exclusão de Conta</h2>
              <p className="text-muted-foreground leading-relaxed">
                A exclusão de conta é diferente do cancelamento de assinatura:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li><strong>Cancelar assinatura:</strong> Você para de pagar, mas mantém a conta e dados</li>
                <li><strong>Excluir conta:</strong> Todos os seus dados são permanentemente removidos</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-2">
                Se você solicitar exclusão de conta enquanto tiver uma assinatura ativa, 
                cancelaremos a assinatura automaticamente, mas não haverá reembolso do período restante.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">7. Disputas de Pagamento</h2>
              <p className="text-muted-foreground leading-relaxed">
                Se você não reconhecer uma cobrança, entre em contato conosco antes de abrir uma 
                disputa com sua operadora de cartão. Isso nos permite resolver a situação mais rapidamente. 
                Disputas abertas diretamente com a operadora podem resultar em suspensão temporária da conta.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">8. Alterações nesta Política</h2>
              <p className="text-muted-foreground leading-relaxed">
                Podemos atualizar esta política periodicamente. Alterações serão comunicadas por email 
                ou através de aviso na plataforma. Transações realizadas antes da alteração seguem 
                a política vigente no momento da transação.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground">9. Contato</h2>
              <p className="text-muted-foreground leading-relaxed">
                Para dúvidas sobre cancelamento, reembolso ou qualquer questão financeira:
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Email:</strong>{" "}
                <a href="mailto:jmkoygg@gmail.com" className="text-primary hover:underline">
                  jmkoygg@gmail.com
                </a>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                Respondemos todas as solicitações em até 2 dias úteis.
              </p>
            </section>

            {/* Summary Box */}
            <section className="mt-8 rounded-lg border bg-muted/50 p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3">Resumo Rápido</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">✓</span>
                  Cancele a qualquer momento pelo painel
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">✓</span>
                  Acesso Pro continua até o fim do período pago
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold">✓</span>
                  Seus dados são preservados após o cancelamento
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-warning font-bold">!</span>
                  Não há reembolso proporcional após cobrança
                </li>
              </ul>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 bg-card mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          © 2026 FreelaDash. Todos os direitos reservados.
        </div>
      </footer>
    </div>
    </>
  );
}
